import { useState, useEffect } from 'react';
import { Image as ImageIcon } from 'lucide-react';
import { type PreviousProduct, UploadedMethodType, useOnboardingStore } from '../../../store/onboardingStore';
import SearchPreviousProducts from '../../onboarding/components/SearchPreviousProducts';
import { ProductLinkInput } from '../ui/ProductLinkInput';
import { ProductManualInputs } from '../ui/ProductManualInputs';
import { dictionary } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';
import { useGenericImageUpload } from '../../../hooks/useGenericImageUpload';
import { FOLDER_IMAGES, MAX_FILE_SIZE, MAX_IMAGES } from '../../onboarding/const/products';

interface StepProductDataProps {
  isScraping?: boolean;
}

export function StepProductData({ isScraping = false }: StepProductDataProps) {
  const { language } = useAuth();
  const {
    data,
    updateProductData,
    updateVisualContent,
    resetProductDerivedState,
    previousProducts,
    isPreviousProductsLoading,
    fetchPreviousProducts
  } = useOnboardingStore();
  const { productData, visualContent } = data;

  const [activeMethod, setActiveMethod] = useState<'link' | 'manual' | 'previous'>('link');
  const [manualData, setManualData] = useState({
    name: productData.name || '',
    price: productData.price || '0.00',
    description: productData.details || '',
    images: (visualContent?.images || []) as string[]
  });

  const {
    isLoading: isUploadingImages,
    handleImageUpload,
    removeImage: handleRemoveImageFromHook
  } = useGenericImageUpload({
    folder: FOLDER_IMAGES,
    currentImages: manualData.images,
    onImagesUpdate: (images) => {
      setManualData(prev => ({ ...prev, images }));
      updateVisualContent({ images });
    },
    allowMultiple: true,
    maxImages: MAX_IMAGES,
    maxFileSize: MAX_FILE_SIZE
  });

  useEffect(() => {
    if (!productData.warranty) {
      updateProductData({
        ...productData,
        warranty: '30days',
        customWarrantyDays: '30',
        freeShipping: productData.freeShipping ?? true
      });
    }
  }, []);

  useEffect(() => {
    fetchPreviousProducts();
  }, []);

  const handleMethodSelect = (method: 'link' | 'manual' | 'previous') => {
    setActiveMethod(method);

    if (method === 'link') {
      if (
        productData.uploadedMethod !== UploadedMethodType.LINK ||
        productData.name ||
        productData.details ||
        productData.productLink ||
        visualContent.selectedAngle?.length
      ) {
        resetProductDerivedState();
      }
      updateProductData({
        ...productData,
        name: '',
        price: '',
        details: '',
        isManualEntry: false,
        productLink: '',
        providerId: null,
        externalId: null,
        uploadedMethod: UploadedMethodType.LINK
      });
      updateVisualContent({ images: [] });
    } else if (method === 'manual') {
      if (productData.uploadedMethod !== UploadedMethodType.MANUAL) {
        resetProductDerivedState();
      }
      updateProductData({
        ...productData,
        isManualEntry: true,
        productLink: '',
        providerId: null,
        externalId: null,
        uploadedMethod: UploadedMethodType.MANUAL
      });
      setManualData({
        name: productData.name || '',
        price: productData.price || '0.00',
        description: productData.details || '',
        images: visualContent?.images || []
      });
    } else if (method === 'previous') {
      if (productData.uploadedMethod !== UploadedMethodType.PREVIOUS) {
        resetProductDerivedState();
      }
      updateProductData({
        ...productData,
        isManualEntry: false,
        uploadedMethod: UploadedMethodType.PREVIOUS
      });
      if (previousProducts.length === 0 && !isPreviousProductsLoading) {
        fetchPreviousProducts();
      }
    }
  };

  const handleProductLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value !== productData.productLink) {
      resetProductDerivedState();
    }
    updateProductData({
      ...productData,
      productLink: value
    });
  };

  const handleManualDataChange = (field: string, value: string) => {
    setManualData({ ...manualData, [field]: value });

    if (field === 'name') {
      if (value !== productData.name) {
        resetProductDerivedState();
      }
      updateProductData({ ...productData, name: value });
    } else if (field === 'price') {
      updateProductData({ ...productData, price: value });
    } else if (field === 'description') {
      if (value !== productData.details) {
        resetProductDerivedState();
      }
      updateProductData({ ...productData, details: value });
    }
  };

  const handleRemoveImage = (index: number) => {
    handleRemoveImageFromHook(index);
  };

  const handlePreviousProductSelect = (selectedProduct: PreviousProduct) => {
    if (selectedProduct.id !== productData.externalId) {
      resetProductDerivedState();
    }

    updateProductData({
      name: selectedProduct.name,
      price: String(selectedProduct.price),
      details: selectedProduct.description || '',
      isManualEntry: false,
      externalId: selectedProduct.id,
      providerId: 'INTERNAL_FLUXI',
      uploadedMethod: UploadedMethodType.PREVIOUS
    });

    if (selectedProduct.images && selectedProduct.images.length > 0) {
      updateVisualContent({
        images: selectedProduct.images
      });
    }
  };

  return (
    <div className={`${activeMethod === 'manual' ? 'max-w-3xl flex flex-col flex-1 min-h-0' : 'max-w-lg'} mx-auto`}>
      <div className={`text-center mb-6 min-h-[5.5rem] flex items-center justify-center ${activeMethod === 'manual' ? 'px-40' : ''}`}>
        <h1 className="text-4xl font-bold mb-2">
          <span className="text-gray-900">{dictionary["load-a-product-to-create-your-page"][language]}</span>
          <span className="text-fluxi">{dictionary["create-your-page"][language]}</span>
        </h1>
      </div>

      <div id="chooseOption-target" className="flex gap-3 justify-center mb-6">
        <button
          type="button"
          disabled={isScraping}
          onClick={() => handleMethodSelect('link')}
          className={`px-6 py-3 rounded-full font-medium transition-all border-2 flex items-center gap-2 disabled:opacity-60 disabled:pointer-events-none ${activeMethod === 'link'
            ? 'bg-[#2B3674] text-white border-[#2B3674]'
            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
            }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          {dictionary["link"][language]}
        </button>
        <button
          type="button"
          disabled={isScraping}
          onClick={() => handleMethodSelect('manual')}
          className={`px-6 py-3 rounded-full font-medium transition-all border-2 flex items-center gap-2 disabled:opacity-60 disabled:pointer-events-none ${activeMethod === 'manual'
            ? 'bg-[#2B3674] text-white border-[#2B3674]'
            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
            }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          {dictionary["upload-photo"][language]}
        </button>
        <button
          type="button"
          disabled={isScraping}
          onClick={() => handleMethodSelect('previous')}
          className={`px-6 py-3 rounded-full font-medium transition-all border-2 flex items-center gap-2 disabled:opacity-60 disabled:pointer-events-none ${activeMethod === 'previous'
            ? 'bg-[#2B3674] text-white border-[#2B3674]'
            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
            }`}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {dictionary["your-products"][language]}
        </button>
      </div>

      {activeMethod === 'link' && (
        <div className="max-w-xl mx-auto space-y-4">
          <ProductLinkInput
            value={productData.productLink || ''}
            onChange={(value) => handleProductLinkChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>)}
            placeholder={dictionary["paste-product-url-placeholder"][language]}
          />
        </div>
      )}

      {activeMethod === 'manual' && (
        <div className="flex-1 min-h-0 mt-1">
          <ProductManualInputs
            manualData={manualData}
            handleImageUpload={handleImageUpload}
            handleRemoveImage={handleRemoveImage}
            handleManualDataChange={handleManualDataChange}
            isUploadingImages={isUploadingImages}
          />
        </div>
      )}

      {activeMethod === 'previous' && (
        <div className="max-w-xl mx-auto space-y-4">
          {isPreviousProductsLoading ? (
            <div className="text-center py-8">
              <div className="inline-block w-8 h-8 border-4 border-gray-200 border-t-fluxi rounded-full animate-spin" />
              <p className="mt-3 text-gray-600">{dictionary["loading-products"][language]}</p>
            </div>
          ) : previousProducts.length === 0 ? (
            <div className="text-center py-8">
              <ImageIcon className="w-16 h-16 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-600">{dictionary["no-previous-products-available"][language]}</p>
            </div>
          ) : (
            <>
              <SearchPreviousProducts
                previousProducts={previousProducts}
                handlePreviousProductSelect={handlePreviousProductSelect}
                isPreviousProductsLoading={isPreviousProductsLoading}
                selectedProductId={productData.externalId}
              />
            </>
          )}
        </div>
      )}
    </div>
  );
}
